import { useState, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery, useMutation } from '@tanstack/react-query'
import { useTopBar } from '@/contexts/TopBarContext'
import { adminApi, type ClientSummaryRow } from '@/api/admin'
import { EmailPreviewModal } from '@/components/shared/EmailPreviewModal'
import {
  AlertTriangle, Clock, CalendarDays, Building2,
  BookOpen, ChevronUp, ChevronDown, ChevronsUpDown,
  Search, Receipt, FileQuestion, Award, Bell,
} from 'lucide-react'
import { useAdminDates } from '@/hooks/useDates'

// ─── helpers ─────────────────────────────────────────────────────────────────

// ─── small shared components ─────────────────────────────────────────────────

type CardColor = 'violet' | 'blue' | 'red' | 'amber' | 'sky' | 'orange'

function StatCard({
  label, value, sub, icon: Icon, color, tinted = false, onClick,
}: {
  label: string
  value: React.ReactNode
  sub?: string
  icon: React.ElementType
  color: CardColor
  /** When true, applies the coloured background and coloured number — use for actionable non-zero values */
  tinted?: boolean
  onClick?: () => void
}) {
  const iconBadge: Record<CardColor, string> = {
    violet: 'bg-violet-100 text-violet-600',
    blue:   'bg-blue-100 text-blue-600',
    red:    'bg-red-100 text-red-600',
    amber:  'bg-amber-100 text-amber-600',
    sky:    'bg-sky-100 text-sky-600',
    orange: 'bg-orange-100 text-orange-600',
  }
  const cardBg: Record<CardColor, string> = {
    violet: 'border-violet-200 bg-violet-50',
    blue:   'border-blue-200 bg-blue-50',
    red:    'border-red-200 bg-red-50',
    amber:  'border-amber-200 bg-amber-50',
    sky:    'border-sky-200 bg-sky-50',
    orange: 'border-orange-200 bg-orange-50',
  }
  const accentNum: Record<CardColor, string> = {
    violet: 'text-violet-700',
    blue:   'text-blue-700',
    red:    'text-red-700',
    amber:  'text-amber-700',
    sky:    'text-sky-700',
    orange: 'text-orange-700',
  }
  // Numbers that are always coloured (non-alert cards like Clients / Staff)
  const alwaysColoured = color === 'violet' || color === 'blue'
  const numCls = (alwaysColoured || tinted) ? accentNum[color] : 'text-gray-700'

  return (
    <div
      className={`rounded-xl border p-5 flex flex-col gap-2 transition-shadow ${tinted ? cardBg[color] : 'border-gray-200 bg-white'} ${onClick ? 'cursor-pointer hover:shadow-sm' : ''}`}
      onClick={onClick}
    >
      <div className="flex items-center justify-between">
        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">{label}</p>
        <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${iconBadge[color]}`}>
          <Icon size={15} />
        </div>
      </div>
      <p className={`text-3xl font-bold tabular-nums leading-none ${numCls}`}>{value}</p>
      {sub && <p className="text-xs text-gray-400">{sub}</p>}
    </div>
  )
}

function BookingStatusBadge({ status }: { status: string }) {
  const map: Record<string, string> = {
    Booked:    'bg-blue-100 text-blue-700',
    Completed: 'bg-green-100 text-green-700',
    Cancelled: 'bg-red-100 text-red-600',
    Rejected:  'bg-amber-100 text-amber-700',
  }
  return (
    <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${map[status] ?? 'bg-gray-100 text-gray-600'}`}>
      {status}
    </span>
  )
}

function ClientStatusBadge({ overdue, dueSoon }: { overdue: number; dueSoon: number }) {
  if (overdue > 0)
    return <span className="inline-flex items-center gap-1.5 px-2 py-1 rounded-full text-xs font-semibold bg-red-100 text-red-700"><span className="w-1.5 h-1.5 rounded-full bg-red-500 shrink-0" />Overdue</span>
  if (dueSoon > 0)
    return <span className="inline-flex items-center gap-1.5 px-2 py-1 rounded-full text-xs font-semibold bg-amber-100 text-amber-700"><span className="w-1.5 h-1.5 rounded-full bg-amber-400 shrink-0" />Due soon</span>
  return <span className="inline-flex items-center gap-1.5 px-2 py-1 rounded-full text-xs font-semibold bg-green-100 text-green-700"><span className="w-1.5 h-1.5 rounded-full bg-green-500 shrink-0" />All current</span>
}

// ─── sortable table header ────────────────────────────────────────────────────

type SortDir = 'asc' | 'desc'
type SortKey = 'clientName' | 'totalStaff' | 'totalCerts' | 'overdue' | 'dueSoon'

function SortTh({
  col, label, current, dir, onSort, className = '',
}: {
  col: SortKey
  label: string
  current: SortKey
  dir: SortDir
  onSort: (col: SortKey) => void
  className?: string
}) {
  const active = current === col
  return (
    <th
      className={`px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider cursor-pointer select-none hover:text-gray-800 transition-colors ${className}`}
      onClick={() => onSort(col)}
    >
      <span className="inline-flex items-center gap-1">
        {label}
        {active
          ? dir === 'asc' ? <ChevronUp size={13} /> : <ChevronDown size={13} />
          : <ChevronsUpDown size={13} className="opacity-30" />}
      </span>
    </th>
  )
}

// ─── main page ────────────────────────────────────────────────────────────────

export default function ComplianceDashboardPage() {
  const { formatDate } = useAdminDates()
  const fmtDate = (d: string) => {
    return formatDate(d + 'T00:00:00', {
      day: 'numeric', month: 'short', year: 'numeric',
    })
  }

  useTopBar(<span className="text-sm font-medium text-gray-900">Overview</span>)
  const navigate = useNavigate()
  const [search, setSearch] = useState('')
  const [sortKey, setSortKey] = useState<SortKey>('clientName')
  const [sortDir, setSortDir] = useState<SortDir>('asc')
  const [notifyRow, setNotifyRow] = useState<ClientSummaryRow | null>(null)

  const { data, isLoading } = useQuery({
    queryKey: ['admin-dashboard'],
    queryFn: () => adminApi.getAdminDashboard().then(r => r.data),
  })

  const { data: emailTemplates } = useQuery({
    queryKey: ['email-templates'],
    queryFn: () => adminApi.getEmailTemplates().then(r => r.data),
  })

  const { data: companyProfile } = useQuery({
    queryKey: ['company-profile'],
    queryFn: () => adminApi.getCompanyProfile().then(r => r.data),
  })

  const expiryTemplate = emailTemplates?.find(t => t.type === 'expiry_warning')

  const notifyDefaults = useMemo(() => {
    if (!notifyRow || !expiryTemplate) return { subject: '', body: '' }
    const companyName = companyProfile?.displayName ?? ''
    const sub = (s: string) => s
      .replace(/\{\{companyName\}\}/g, companyName)
      .replace(/\{\{contactName\}\}/g, notifyRow.contactName ?? notifyRow.clientName)
      .replace(/\{\{clientName\}\}/g, notifyRow.clientName)
    return { subject: sub(expiryTemplate.subject), body: sub(expiryTemplate.body) }
  }, [notifyRow, expiryTemplate, companyProfile])

  const notifyMutation = useMutation({
    mutationFn: ({ subject, body }: { subject: string; body: string }) =>
      adminApi.sendClientExpiryWarning(notifyRow!.clientId, { subject, body }),
    onSuccess: () => setNotifyRow(null),
    onError: () => alert('Failed to send notification.'),
  })

  function handleSort(col: SortKey) {
    if (col === sortKey) setSortDir(d => d === 'asc' ? 'desc' : 'asc')
    else { setSortKey(col); setSortDir('asc') }
  }

  const filteredClients = useMemo(() => {
    if (!data) return []
    const q = search.toLowerCase()
    const filtered = q
      ? data.clients.filter(c => c.clientName.toLowerCase().includes(q))
      : data.clients

    return [...filtered].sort((a, b) => {
      const av = a[sortKey]
      const bv = b[sortKey]
      const cmp = typeof av === 'string'
        ? (av as string).localeCompare(bv as string)
        : (av as number) - (bv as number)
      return sortDir === 'asc' ? cmp : -cmp
    })
  }, [data, search, sortKey, sortDir])

  if (isLoading) return <p className="text-gray-500 py-10 text-center">Loading…</p>
  if (!data) return null

  return (
    <>
    <EmailPreviewModal
      isOpen={!!notifyRow}
      onClose={() => setNotifyRow(null)}
      onSend={(subject, body) => notifyMutation.mutate({ subject, body })}
      isSending={notifyMutation.isPending}
      defaultSubject={notifyDefaults.subject}
      defaultBody={notifyDefaults.body}
      recipientEmail={notifyRow?.contactEmail ?? ''}
      title={`Notify ${notifyRow?.clientName ?? ''}`}
    />
    <div className="space-y-8">
      {/* ── Stat cards ── */}
      <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4">
        <StatCard label="Clients" value={data.totalClients} icon={Building2} color="violet" onClick={() => navigate('/admin/clients')} />
        <StatCard label="Certificates issued" value={data.totalCertificatesIssued} icon={Award} color="blue" onClick={() => navigate('/admin/clients')} />
        <StatCard
          label="Overdue"
          value={data.totalOverdue}
          icon={AlertTriangle}
          color="red"
          tinted={data.totalOverdue > 0}
        />
        <StatCard
          label="Due soon"
          value={data.totalDueSoon}
          icon={Clock}
          color="amber"
          tinted={data.totalDueSoon > 0}
          sub="within 30 days"
        />
        <StatCard
          label="Enquiries"
          value={data.pendingEnquiries}
          icon={FileQuestion}
          color="sky"
          tinted={data.pendingEnquiries > 0}
          sub="pending"
          onClick={data.pendingEnquiries > 0 ? () => navigate('/admin/bookings?tab=enquiries') : undefined}
        />
        <StatCard
          label="Invoices"
          value={data.unpaidInvoicesCount}
          icon={Receipt}
          color="orange"
          tinted={data.unpaidInvoicesCount > 0}
          sub="unpaid / part-paid"
          onClick={data.unpaidInvoicesCount > 0 ? () => navigate('/admin/invoices') : undefined}
        />
      </div>

      {/* ── Client compliance table ── */}
      <section>
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-base font-semibold text-gray-800">Client compliance</h2>
          <div className="relative">
            <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Search clients…"
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="pl-8 pr-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 w-52"
            />
          </div>
        </div>

        <div className="bg-white border border-gray-200 rounded-xl overflow-hidden shadow-sm">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <SortTh col="clientName" label="Client"      current={sortKey} dir={sortDir} onSort={handleSort} className="text-left" />
                <SortTh col="totalStaff" label="Staff"       current={sortKey} dir={sortDir} onSort={handleSort} className="text-right" />
                <SortTh col="totalCerts" label="Certs"       current={sortKey} dir={sortDir} onSort={handleSort} className="text-right" />
                <SortTh col="overdue"    label="Overdue"     current={sortKey} dir={sortDir} onSort={handleSort} className="text-right" />
                <SortTh col="dueSoon"    label="Due soon"    current={sortKey} dir={sortDir} onSort={handleSort} className="text-right" />
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Status</th>
                <th className="px-4 py-3" />
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {filteredClients.length === 0 && (
                <tr>
                  <td colSpan={7} className="text-center text-gray-400 py-10">
                    {search ? 'No clients match your search.' : 'No client data yet.'}
                  </td>
                </tr>
              )}
              {filteredClients.map(row => (
                <tr
                  key={row.clientId}
                  className="hover:bg-slate-50 cursor-pointer transition-colors"
                  onClick={() => navigate(`/admin/clients/${row.clientId}`)}
                >
                  <td className="px-4 py-3 font-medium text-gray-900">{row.clientName}</td>
                  <td className="px-4 py-3 text-right text-gray-600">{row.totalStaff}</td>
                  <td className="px-4 py-3 text-right text-gray-600">{row.totalCerts}</td>
                  <td className="px-4 py-3 text-right">
                    {row.overdue > 0
                      ? <span className="inline-flex items-center gap-1 font-semibold text-red-600"><AlertTriangle size={12} />{row.overdue}</span>
                      : <span className="text-gray-400">—</span>}
                  </td>
                  <td className="px-4 py-3 text-right">
                    {row.dueSoon > 0
                      ? <span className="inline-flex items-center gap-1 font-semibold text-amber-600"><Clock size={12} />{row.dueSoon}</span>
                      : <span className="text-gray-400">—</span>}
                  </td>
                  <td className="px-4 py-3">
                    {row.totalCerts > 0
                      ? <ClientStatusBadge overdue={row.overdue} dueSoon={row.dueSoon} />
                      : <span className="text-xs text-gray-400">No records</span>}
                  </td>
                  <td className="px-4 py-3 text-right">
                    {(row.overdue > 0 || row.dueSoon > 0) && (
                      <button
                        className="inline-flex items-center gap-1 text-xs font-medium text-indigo-600 hover:text-indigo-800 px-2 py-1 rounded hover:bg-indigo-50 transition-colors"
                        onClick={e => { e.stopPropagation(); setNotifyRow(row) }}
                        title="Send expiry reminder to client contact"
                      >
                        <Bell size={13} /> Notify
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {/* ── Lower cards ── */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">

        {/* Upcoming client bookings */}
        <section>
          <h2 className="text-base font-semibold text-gray-800 mb-3 flex items-center gap-2">
            <CalendarDays size={16} className="text-blue-500" />
            Upcoming bookings
            <span className="text-xs font-normal text-gray-400">(next 60 days)</span>
          </h2>
          {data.upcomingBookings.length === 0
            ? (
              <div className="bg-white border border-gray-200 rounded-xl p-8 text-center text-gray-400 text-sm">
                No upcoming bookings in the next 60 days.
              </div>
            )
            : (
              <div className="bg-white border border-gray-200 rounded-xl overflow-hidden shadow-sm">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 border-b border-gray-200">
                    <tr>
                      <th className="px-4 py-2.5 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Date</th>
                      <th className="px-4 py-2.5 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Course</th>
                      <th className="px-4 py-2.5 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Client</th>
                      <th className="px-4 py-2.5 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider">Places</th>
                      <th className="px-4 py-2.5 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Status</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {data.upcomingBookings.map(b => (
                      <tr
                        key={b.bookingId}
                        className="hover:bg-slate-50 cursor-pointer transition-colors"
                        onClick={() => navigate(`/admin/bookings/${b.bookingId}`)}
                      >
                        <td className="px-4 py-2.5 text-gray-600 whitespace-nowrap">{fmtDate(b.bookingDate)}</td>
                        <td className="px-4 py-2.5 font-medium text-gray-900">{b.courseName}</td>
                        <td className="px-4 py-2.5 text-gray-600">{b.clientName}</td>
                        <td className="px-4 py-2.5 text-right text-gray-600">{b.places}</td>
                        <td className="px-4 py-2.5"><BookingStatusBadge status={b.status} /></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <div className="px-4 py-2.5 border-t border-gray-100 bg-gray-50">
                  <button
                    className="text-xs text-indigo-600 hover:text-indigo-800 font-medium cursor-pointer"
                    onClick={() => navigate('/admin/bookings')}
                  >
                    View all bookings →
                  </button>
                </div>
              </div>
            )}
        </section>

        {/* Top courses */}
        <section>
          <h2 className="text-base font-semibold text-gray-800 mb-3 flex items-center gap-2">
            <BookOpen size={16} className="text-indigo-500" />
            Most booked courses
          </h2>
          {data.topCourses.length === 0
            ? (
              <div className="bg-white border border-gray-200 rounded-xl p-8 text-center text-gray-400 text-sm">
                No booking data yet.
              </div>
            )
            : (
              <div className="bg-white border border-gray-200 rounded-xl overflow-hidden shadow-sm">
                {data.topCourses.map((c, i) => {
                  const maxCount = data.topCourses[0].bookingCount
                  const barPct = Math.round((c.bookingCount / maxCount) * 100)
                  return (
                    <div
                      key={c.courseId}
                      className={`px-4 py-3 flex items-center gap-3 ${i < data.topCourses.length - 1 ? 'border-b border-gray-100' : ''}`}
                    >
                      <span className="text-sm font-bold text-gray-300 w-4 shrink-0">{i + 1}</span>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900 truncate">{c.courseName}</p>
                        <div className="mt-1 flex items-center gap-2">
                          <div className="flex-1 h-1.5 rounded-full bg-gray-100 overflow-hidden">
                            <div className="h-full rounded-full bg-indigo-400" style={{ width: `${barPct}%` }} />
                          </div>
                        </div>
                      </div>
                      <div className="text-right shrink-0">
                        <p className="text-sm font-semibold text-gray-800 tabular-nums">{c.bookingCount}</p>
                        <p className="text-xs text-gray-400">{c.totalPlaces} places</p>
                      </div>
                    </div>
                  )
                })}
                <div className="px-4 py-2.5 border-t border-gray-100 bg-gray-50">
                  <button
                    className="text-xs text-indigo-600 hover:text-indigo-800 font-medium cursor-pointer"
                    onClick={() => navigate('/admin/courses')}
                  >
                    Manage courses →
                  </button>
                </div>
              </div>
            )}
        </section>
      </div>
    </div>
    </>
  )
}
